import {
  COLUMN_NAME_CLICKS,
  COLUMN_NAME_CPA,
  COLUMN_NAME_CTR,
  COLUMN_NAME_IMPRESSIONS,
  COLUMN_NAME_RESULTS,
  COLUMN_NAME_SPEND,
  FACEBOOK,
  INSTAGRAM,
  THREADS,
  VALUE_NUMBER,
  VALUE_WITH_CURRENCY_MANUAL_FORMAT,
  VALUE_WITH_PERCENTAGE,
} from "../../../../../i18n/keysTranslations";
import { renderMetricValue } from "../../../../../utils/renderMetricValue";
import {
  MISSING_METRIC_LABEL,
  finiteMetric,
  getInsightLeaderRow,
  getInsightPerformanceMetricKey,
} from "../components/performingAssetsMetrics";

/**
 * Exclude Meta age breakdown rows not shown in the audience table:
 * `unknown`, and aggregate bucket `65+` (case-insensitive; spaces ignored).
 */
//eslint-disable-next-line
export const filterAgeDemographicInsightsRows = (rows) =>
  (rows ?? []).filter((row) => {
    const age = row?.age;
    if (age === null || age === undefined) return true;
    const normalized = String(age).trim().toLowerCase().replace(/\s/g, "");
    if (normalized === "unknown") return false;
    if (normalized === "65+") return false;
    return true;
  });

//eslint-disable-next-line
export const filterGenderInsightsRowsWithoutUnknown = (rows) =>
  (rows ?? []).filter((row) => {
    const gender = row?.gender;
    if (gender === null || gender === undefined) return true;
    return String(gender).trim().toLowerCase() !== "unknown";
  });

//eslint-disable-next-line
export const PUBLISHER_PLATFORM_LABEL_KEYS = {
  facebook: FACEBOOK,
  instagram: INSTAGRAM,
  threads: THREADS,
};

const formatPublisherPlatformFallbackLabel = (publisherPlatform) => {
  const normalized = String(publisherPlatform).trim().toLowerCase();
  if (!normalized) return "—";
  return normalized
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

//eslint-disable-next-line
export const getPublisherPlatformRowLabel = ({ publisherPlatform, t }) => {
  const normalized = String(publisherPlatform ?? "")
    .trim()
    .toLowerCase();
  const labelKey = PUBLISHER_PLATFORM_LABEL_KEYS[normalized];
  if (labelKey) return t(labelKey);
  return formatPublisherPlatformFallbackLabel(publisherPlatform);
};

//eslint-disable-next-line
export const sortPublisherPlatformInsightsRows = (rows) =>
  [...(rows ?? [])]
    .filter((row) => {
      const publisherPlatform = row?.publisherPlatform;
      if (!publisherPlatform) return false;
      return String(publisherPlatform).trim().toLowerCase() !== "unknown";
    })
    .toSorted((a, b) => (b?.spend ?? 0) - (a?.spend ?? 0));

const METRIC_COLUMNS = [
  {
    key: "impressions",
    labelKey: COLUMN_NAME_IMPRESSIONS,
    format: "number",
    accessor: (row) => row.impressions,
  },
  {
    key: "click",
    labelKey: COLUMN_NAME_CLICKS,
    format: "number",
    accessor: (row) => row.click,
  },
  {
    key: "ctr",
    labelKey: COLUMN_NAME_CTR,
    format: "percentage",
    accessor: (row) => row.ctr,
  },
  {
    key: "spend",
    labelKey: COLUMN_NAME_SPEND,
    format: "spend",
    accessor: (row) => row.spend,
  },
  {
    key: "mainAction",
    labelKey: COLUMN_NAME_RESULTS,
    format: "number",
    accessor: (row) => row.mainAction,
  },
  {
    key: "costPerMainAction",
    labelKey: COLUMN_NAME_CPA,
    format: "cpa",
    accessor: (row) => row.costPerMainAction,
  },
];

const formatMetricCell = ({ row, metric, t, getCostByMainCurrency }) => {
  const raw = metric.accessor(row);
  const currency = row.currency;
  const numeric = finiteMetric(raw);

  switch (metric.format) {
    case "number":
      if (numeric === null) return MISSING_METRIC_LABEL;
      return t(VALUE_NUMBER, { value: numeric });
    case "percentage":
      if (numeric === null) return MISSING_METRIC_LABEL;
      return t(VALUE_WITH_PERCENTAGE, { value: numeric });
    case "spend": {
      if (numeric === null) return MISSING_METRIC_LABEL;
      const { cost, currency: outCurrency } = getCostByMainCurrency({
        cost: numeric,
        currency,
      });
      if (finiteMetric(cost) === null) return MISSING_METRIC_LABEL;
      return t(VALUE_WITH_CURRENCY_MANUAL_FORMAT, {
        value: cost,
        currency: outCurrency,
      });
    }
    case "cpa": {
      const mainAction = finiteMetric(row.mainAction);
      if (mainAction === null || mainAction <= 0) return "-";
      if (numeric === null) return MISSING_METRIC_LABEL;
      const { cost, currency: outCurrency } = getCostByMainCurrency({
        cost: numeric,
        currency,
      });
      if (finiteMetric(cost) === null) return MISSING_METRIC_LABEL;
      return renderMetricValue(cost, "costPerMainAction", outCurrency);
    }
    default: {
      if (raw === undefined || raw === null) return MISSING_METRIC_LABEL;
      return String(raw);
    }
  }
};

const PRIMARY_INSIGHT_KEYS = ["impressions", "ctr"];

// Results, then clicks, then impressions. Never CTR, which can be missing.
export const getPerformanceMetricKey = (rows = []) =>
  getInsightPerformanceMetricKey(rows);

export const getBestPerformanceRow = (rows = []) => getInsightLeaderRow(rows);

export const buildInsightMetricCells = ({ row, t, getCostByMainCurrency }) =>
  METRIC_COLUMNS.map((metric) => ({
    key: metric.key,
    label: t(metric.labelKey),
    value: formatMetricCell({ row, metric, t, getCostByMainCurrency }),
  }));

export const splitInsightMetrics = (cells = []) => ({
  primary: cells.filter((cell) => PRIMARY_INSIGHT_KEYS.includes(cell.key)),
  secondary: cells.filter((cell) => !PRIMARY_INSIGHT_KEYS.includes(cell.key)),
});
